import { defineStore } from 'pinia';
import {
	type Project,
	ProjectBudget as ProjectBudgetType,
	ProjectBudget,
	type Contact,
	type ProjectPermissions,
	ProjectRevenueCalcModel,
	RetainerFrequency,
	ProjectType,
	type CompanyAccount,
	type Quote,
	type User,
	type UserAssignSelectUserFragment,
	ProjectPhaseTypeEnum,
} from '@/generated/graphql';
import { type BudgetAndPricingData } from '../components/ProjectBudgetAndPricing/types';
import {
	DefaultRetainerNoOfPeriods,
	RevenueTypeId,
	DefaultRetainerFrequency,
} from '../components/ProjectBudgetAndPricing/constants';
import { type PhaseAndMilestoneListItemType } from '@/features/ProjectPhase';
import { dateToMysqlString } from '@/composables/settings/useFormatter/formatterUtils';
import { resetTime } from '@/utils/formatting';
import { addDays } from 'date-fns';
import type { ValueOf } from '@/types';
import {
	FIELD_PROJECT_NAME,
	FIELD_PROJECT_NUMBER,
	ProjectModifySections,
	ProjectModifyTabs,
} from '../components/ProjectModifyModal/constants';
import { DEFAULT_COLOR } from '../constants';
import { type LiterallyAnything } from '@/types/utils';

export type ProjectModifyModalStoreType = Pick<
	Project,
	| 'name'
	| 'isPersonal'
	| 'color'
	| 'isPrivate'
	| 'budgetType'
	| 'duration'
	| 'useBillsExpensesInCalculations'
	| 'costBudget'
	| 'revenueBudget'
	| 'priceList'
	| 'localPriceList'
	| 'budgetSums'
	| 'quotedBudgetSums'
	| 'useRoles'
	| 'retainer'
	| 'shouldDisableCalculationType'
> & {
	id: string | null;
	accountId: string | undefined;
	number: string | undefined;
	date: string | undefined | null;
	deadline: string | undefined | null;
	company: Pick<Contact, 'id' | 'name' | 'unpaidInvoices'>;
	manager: UserAssignSelectUserFragment | undefined | null;
	memberIds: Array<User['id']>;
	cupoUserIds: Array<User['id']>;
	permissions: Pick<ProjectPermissions, 'canModify' | 'canDelete'>;
	templateId: Project['id'];
	quoteId: Quote['id'];
	clientId: Contact['id'];
	projectType: ProjectType;
	accounts: Array<CompanyAccount>;
	accountsSnapshot: Array<CompanyAccount>;
	budgetAndPricingData: Pick<
		BudgetAndPricingData,
		| 'billingPeriodCount'
		| 'billingPeriodUnit'
		| 'billingPeriodFrequency'
		| 'isPeriodDurationEnabled'
		| 'budgetMode'
		| 'totalDuration'
		| 'totalBudget'
		| 'durationPerPeriod'
		| 'budgetPerPeriod'
		| 'isEstimatedCostsEnabled'
		| 'bills'
		| 'expenses'
		| 'laborCost'
		| 'isDeductedFromBudget'
		| 'revenueBudgetId'
		| 'costBudgetId'
		| 'selectedPriceListId'
		| 'localPriceListId'
		| 'revenueType'
		| 'revenueCalcModel'
	>;
	initialCustomFields: Record<string, string>;
	customFields: Record<string, string>;
	customFieldCheckTextFields: Record<string, string>;
	customFieldRelationFields: Record<string, string>;
	customFieldMultiSelectNames: string[];
	tags: Array<string>;
	userDefinedRequiredFields: {
		deadline: boolean;
	};
	uniqueFieldWithError: string;
	setQuoteAsConfirmed: boolean;
	hasBackToBackPhases: boolean;
	projectPhases: PhaseAndMilestoneListItemType[];
	hasChanged: boolean;
	isOpen: boolean;
	currentTab: ValueOf<typeof ProjectModifyTabs>;
	customFieldsBlockHTML: string;
	tagsBlockHTML: string;
	numberOfTasks: number;
	validationErrors: Record<
		| typeof ProjectModifyTabs.GENERAL
		| typeof ProjectModifySections.CUSTOM_FIELDS,
		Array<LiterallyAnything>
	>;
	openGeneralTabSections: Record<
		ValueOf<typeof ProjectModifySections>,
		boolean
	>;
};

export const getProjectModifyModalDefaultState =
	(): ProjectModifyModalStoreType => ({
		isOpen: false,
		currentTab: ProjectModifyTabs.GENERAL,
		customFieldsBlockHTML: '',
		tagsBlockHTML: '',
		accountId: '',
		id: null,
		name: '',
		number: undefined,
		isPersonal: false,
		date: dateToMysqlString(new Date()),
		deadline: '',
		company: {
			id: '',
			name: '',
			unpaidInvoices: null,
		},
		projectType: ProjectType.Regular,
		color: DEFAULT_COLOR,
		manager: undefined,
		memberIds: [],
		cupoUserIds: [],
		permissions: {
			canModify: true,
			canDelete: false,
		},
		isPrivate: null,
		budgetType: ProjectBudget.Quote,
		duration: null,
		useBillsExpensesInCalculations: false,
		retainer: undefined,
		revenueBudget: undefined,
		costBudget: undefined,
		quotedBudgetSums: undefined,
		budgetSums: undefined,
		priceList: undefined,
		localPriceList: undefined,
		useRoles: false,
		shouldDisableCalculationType: false,
		templateId: '',
		quoteId: '',
		clientId: '',
		budgetAndPricingData: {
			billingPeriodCount: DefaultRetainerNoOfPeriods,
			billingPeriodFrequency: DefaultRetainerFrequency,
			billingPeriodUnit: RetainerFrequency.Month,
			isPeriodDurationEnabled: false,
			budgetMode: ProjectBudgetType.Quote,
			totalDuration: '',
			totalBudget: '',
			durationPerPeriod: '',
			budgetPerPeriod: '',
			isEstimatedCostsEnabled: false,
			bills: '',
			expenses: '',
			laborCost: '',
			isDeductedFromBudget: false,
			revenueBudgetId: '',
			costBudgetId: '',
			selectedPriceListId: undefined,
			localPriceListId: undefined,
			revenueType: RevenueTypeId.ROLES,
			revenueCalcModel: ProjectRevenueCalcModel.FixedFee,
		},
		accounts: [],
		initialCustomFields: {},
		customFields: {},
		customFieldCheckTextFields: {},
		customFieldRelationFields: {},
		customFieldMultiSelectNames: [],
		tags: [],
		userDefinedRequiredFields: {
			deadline: false,
		},
		uniqueFieldWithError: '',
		setQuoteAsConfirmed: true,
		accountsSnapshot: [],
		hasBackToBackPhases: false,
		projectPhases: [],
		hasChanged: false,
		numberOfTasks: 0,
		validationErrors: {
			[ProjectModifyTabs.GENERAL]: [],
			[ProjectModifySections.CUSTOM_FIELDS]: [],
		},
		openGeneralTabSections: {
			[ProjectModifySections.TAGS]: false,
			[ProjectModifySections.ENTITIES]: false,
			[ProjectModifySections.CUSTOM_FIELDS]: false,
		},
	});

export const useProjectModifyModalStore = defineStore('projectModifyModal', {
	state: (): ProjectModifyModalStoreType =>
		getProjectModifyModalDefaultState(),
	getters: {
		showRequiredFieldMarkerForName(state) {
			return state.name === '';
		},
		showRequiredFieldMarkerForContact(state) {
			return !state.isPersonal && state.company.id === '';
		},
		showRequiredFieldMarkerForNumber(state) {
			return ['', undefined].includes(state.number);
		},
		showRequiredFieldMarkerForDate(state) {
			return ['', undefined, null].includes(state.date);
		},
		showRequiredFieldMarkerForDeadline(state) {
			return (
				state.userDefinedRequiredFields.deadline &&
				['', undefined, null].includes(state.deadline)
			);
		},
		isNameFieldUniqueError(state) {
			return state.uniqueFieldWithError === FIELD_PROJECT_NAME;
		},
		isNumberFieldUniqueError(state) {
			return state.uniqueFieldWithError === FIELD_PROJECT_NUMBER;
		},
	},
	actions: {
		open(args?: {
			projectId?: Project['id'];
			tab?: ValueOf<typeof ProjectModifyTabs>;
			clientId?: Contact['id'];
			quoteId?: Quote['id'];
			initialCustomFields?: Record<string, string>;
		}) {
			this.isOpen = true;

			if (args?.projectId) {
				this.id = args.projectId;
			}

			if (args?.tab) {
				this.currentTab = args.tab;
			}

			if (args?.clientId) {
				this.clientId = args.clientId;
			}

			if (args?.quoteId) {
				this.quoteId = args.quoteId;
			}

			if (args?.initialCustomFields) {
				this.initialCustomFields = args.initialCustomFields;
			}
		},
		clearCupoUsers() {
			const hasCupoUsersInMembers = this.memberIds.some((memberId) =>
				this.cupoUserIds.includes(memberId)
			);

			if (hasCupoUsersInMembers) {
				this.memberIds = this.memberIds.filter(
					(memberId) => !this.cupoUserIds.includes(memberId)
				);
			}

			this.cupoUserIds = [];
		},
		updatePhaseDatesBasedOnProjectStart(
			date: string | null | undefined,
			phases: PhaseAndMilestoneListItemType[]
		): PhaseAndMilestoneListItemType[] {
			if (!date || phases.length === 0) {
				return phases;
			}

			const today = resetTime(new Date());
			const projectStartDate = resetTime(date);
			const baseDate =
				projectStartDate > today ? projectStartDate : today;

			return phases.map((phase) => {
				const updatedPhase = { ...phase };

				if (updatedPhase.type === ProjectPhaseTypeEnum.Phase) {
					const startDays = parseInt(updatedPhase.startDays || '');
					const endDays = parseInt(updatedPhase.endDays || '');

					if (!isNaN(startDays)) {
						updatedPhase.startDate = dateToMysqlString(
							addDays(baseDate, startDays)
						);
					}

					if (!isNaN(endDays) && updatedPhase.startDate) {
						updatedPhase.endDate = dateToMysqlString(
							addDays(resetTime(updatedPhase.startDate), endDays)
						);
					}
				}

				if (updatedPhase.type === ProjectPhaseTypeEnum.Milestone) {
					const endDays = parseInt(updatedPhase.endDays || '');

					if (!isNaN(endDays)) {
						updatedPhase.endDate = dateToMysqlString(
							addDays(baseDate, endDays)
						);
					}
				}

				return updatedPhase;
			});
		},
	},
});
